/**
 * Fazla Mesai (Overtime) ile ilgili TypeScript tip tanımlamaları
 * 
 * Bu dosya overtime sistemi için gerekli tüm interface ve enum tanımlarını içerir.
 * Yönetici ve çalışan panellerinde overtime yönetimi için kullanılır.
 */

// Overtime kaydı için temel interface
export interface OvertimeDto {
  id: number;
  // Çalışan bilgileri
  user: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    profilePicture?: string;
    gender?: 'male' | 'female' | 'other'; // Cinsiyet (profil resmi fallback için)
    primaryDepartment: {
      id: number;
      name: string;
    };
  };
  // Tarih ve zaman bilgileri
  date: string; // YYYY-MM-DD formatında
  overtimeStart: string; // HH:mm formatında
  overtimeEnd: string; // HH:mm formatında
  overtimeDuration: number; // Dakika cinsinden
  // Gerçekleşen overtime bilgileri (tamamlandığında dolu olur)
  actualOvertimeEnd?: string; // HH:mm formatında
  actualOvertimeDuration?: number; // Dakika cinsinden
  // Açıklama ve durum
  description?: string;
  status: OvertimeStatus;
  isCompleted: boolean; // Çalışan gerçekten fazla mesai yaptı mı?
  // Oluşturan kişi bilgileri
  createdBy: 'employee' | 'manager'; // Kim oluşturdu?
  createdByUser?: {
    id: number;
    firstName: string;
    lastName: string;
  };
  // Onaylayan kişi bilgileri
  approvedBy?: {
    id: number;
    firstName: string;
    lastName: string;
  };
  rejectionReason?: string; // Red nedeni (sadece reject durumunda)
  reviewedAt?: string; // Onay/red tarihi
  // Timestamp'ler
  createdAt: string;
  updatedAt: string;
}

// Overtime durumları
export enum OvertimeStatus {
  PENDING = 'pending',   // Beklemede (yönetici onayı bekleniyor)
  APPROVED = 'approved', // Onaylandı
  REJECTED = 'rejected'  // Reddedildi
}

// Çalışana fazla mesai tanımlama için DTO
export interface OvertimeCreateDTO {
  employeeId: number;     // Hangi çalışana tanımlanacak
  date: string;           // Hangi tarihte (YYYY-MM-DD)
  startTime: string;      // Başlangıç saati (HH:mm)
  endTime: string;        // Bitiş saati (HH:mm)
  description?: string;   // Açıklama (opsiyonel)
  autoApprove?: boolean;  // true ise direkt onaylı olarak oluşturulur
}

// Talep onaylama/reddetme için DTO
export interface OvertimeReviewDTO {
  action: 'approve' | 'reject';
  rejectionReason?: string; // Sadece 'reject' durumunda zorunlu
}

// Dashboard özet verisi için interface
export interface OvertimeSummaryDto {
  pending: number;                    // Bekleyen talep sayısı
  approvedToday: number;              // Bugün onaylanan talep sayısı
  approvedThisWeek: number;           // Bu hafta onaylanan talep sayısı
  approvedThisMonth: number;          // Bu ay onaylanan talep sayısı
  rejectedThisMonth: number;          // Bu ay reddedilen talep sayısı
  totalHoursApprovedThisMonth: number; // Bu ay onaylanan toplam saat
  totalCostThisMonth?: number;        // Bu ay toplam maliyet (TL - opsiyonel)
  recentRequests: OvertimeDto[];      // Son talepler (max 5 adet)
}

// Filtreleme için interface
export interface OvertimeFilters {
  startDate?: string;      // Başlangıç tarihi (YYYY-MM-DD)
  endDate?: string;        // Bitiş tarihi (YYYY-MM-DD)
  departmentId?: number;   // Departman ID'si
  employeeId?: number;     // Çalışan ID'si
  status?: 'all' | OvertimeStatus; // Durum filtresi
  page?: number;           // Sayfa numarası
  perPage?: number;        // Sayfa başına kayıt sayısı
}

// Pagination için interface
export interface OvertimePaginationDto {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

// Tüm overtime kayıtları için response interface
export interface OvertimeListResponseDto {
  data: OvertimeDto[];
  pagination: OvertimePaginationDto;
}

// Çalışan tarafından talep oluşturma için DTO
export interface EmployeeOvertimeRequestDTO {
  date: string;         // Hangi tarihte (YYYY-MM-DD)
  startTime: string;    // Başlangıç saati (HH:mm)
  endTime: string;      // Bitiş saati (HH:mm)
  description?: string; // Açıklama (opsiyonel)
}


